<script lang="ts" setup>
import { computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useEvents } from '@/plugins/events';
import { useLowCodeStore } from '@/modules/low-code/common/store/low-code';
import { getIconButton, getVariantButton } from '@/components/common/dynamic-grid/helpers/buttonHelper';
import { BpmnEmitOptions } from '@/modules/low-code/process/services/BpmnService';
import { EventType, FrontAction, FrontActionModal } from '@/components/common/dynamic-grid/helpers/GridEnums';
import GridService, { IMetaModel } from '@/modules/core/common/services/GridService';

const props = defineProps({
  "actions": { default: () => ({}) },
  "except": { default: (): string[] => [] },
  "publicId": { default: '' },
  "url": { default: '' },
  "isOffice": { type: Boolean, default: false },
  "admin": { type: Boolean, default: false }
});

defineOptions({
    name: 'details-action-button',
});

const { $events } = useEvents();
const router = useRouter();
const route = useRoute();
const store = useLowCodeStore();

const filterActions = (actions: Record<string, IMetaModel>, officeChangeProcess: boolean) =>
{
    return Object.values(actions).filter(
        (action) =>
            props.except.includes(action.key) === false &&
            action.type !== 'Export' &&
            action.frontAction != 'InvoiceDescriptionModel' &&
            (!props.isOffice || action.order === (officeChangeProcess ? 1 : 0)) &&
            action.isAdminAction === props.admin
    );
};

const filteredActions = computed(() => filterActions(props.actions, false));

const filteredActionsOfficeChangeProcess = computed(() => filterActions(props.actions, true));

const handleEvent = (action: IMetaModel) =>
{
    const key = action.key;
    const lastIndex = action.key.lastIndexOf('-');
    const actionName = action.key.substring(lastIndex, -1);

    const data: BpmnEmitOptions = {
        id: props.publicId,
        licence: props.url,
        actionName,
        buttonId: action.endpointId,
        displayMode: action.displayMode,
        displaySize: action.displaySize,
        title: action.title,
        buttonName: action.value,
        buttonVariant: getVariantButton(action.type as string),
        buttonIcon: getIconButton(action.type as string),
        formButtonName: action.formButtonName,
    };

    if (key.includes('flow') || key.includes('bpmn'))
    {
        emitProperEvent(action.frontAction, data);

        return;
    }

    if (key.includes('redirect') && action.url)
    {
        router.push(action.url);

        return;
    }

    switch (key)
    {
        case EventType.Edit: {
            if (action.frontAction)
            {
                emitProperEvent(action.frontAction, data);
                break;
            }

            router.push({
                name: `${String(route.matched[1].name || route.name)}-edit`,
                params: { publicId: props.publicId },
            });
            break;
        }
        case EventType.Delete: {
            if (action.frontAction)
            {
                emitProperEvent(action.frontAction, data);
                break;
            }

            handleDelete(props.publicId);
            break;
        }
        default:
            break;
    }
};

const emitProperEvent = (frontAction: string, data: BpmnEmitOptions) =>
{
    switch (frontAction)
    {
        case FrontAction.FireEditCreateModal:
            $events.$emit(FrontActionModal.EditCreate, props.publicId);
            break;
        case FrontAction.FireDeleteModal:
            $events.$emit(FrontActionModal.Delete, props.publicId);
            break;
        case FrontAction.FireFlowAcceptModal:
            $events.$emit(FrontActionModal.FlowAccept, { id: props.publicId, url: props.url });
            break;
        case FrontAction.FireFlowRejectModal:
            $events.$emit(FrontActionModal.FlowReject, { id: props.publicId, url: props.url });
            break;
        case FrontAction.FireFlowSendModal:
            $events.$emit(FrontActionModal.FlowSend, { id: props.publicId, url: props.url });
            break;
        case FrontAction.FireTranslateModal:
            $events.$emit(FrontActionModal.TranslateModal, props.publicId);
            break;

        // Dynamiczny
        case FrontAction.Object:
            if (data.displayMode === 'Form')
            {
                router.push({
                    name: 'module-form-dynamic',
                    params: { licence: data.licence, publicId: data.id },
                    query: { id: data.buttonId },
                });
            }
            else $events.$emit(`${FrontActionModal.Object}-1`, data);

            break;

        // Opis kodami kontrolingowymi - przekaż do akceptacji
        case FrontAction.FinishDescriptionModel:
            $events.$emit(FrontActionModal.FinishDescriptionModel, data);
            break;

        // Podpis dokumentu
        case FrontAction.SignDocumentSimplySign:
            $events.$emit(FrontActionModal.SignDocumentSimplySign, data);
            break;

        case FrontAction.SignDocumentMSzafir:
            $events.$emit(FrontActionModal.SignDocumentMSzafir, data);
            break;

        case FrontAction.SignDocumentSzafirWebModule:
            $events.$emit(FrontActionModal.SignDocumentSzafirWebModule, data);
            break;

        // Uwagi - niewymagane;
        case FrontAction.FlowModel:
            $events.$emit(FrontActionModal.FlowModel, data);
            break;

        // Uwagi - wymagane
        case FrontAction.ReturnModel:
            $events.$emit(FrontActionModal.ReturnModel, data);
            break;

        // Wybór użytkownika
        case FrontAction.ToOwnerModel:
            $events.$emit(FrontActionModal.ToOwnerModel, data);
            break;

        // Wznów obieg
        case FrontAction.ResumeModal:
            $events.$emit(FrontActionModal.ResumeModal, data);
            break;

        // Zmiana akceptantów
        case FrontAction.SetTargetsModal:
            $events.$emit(FrontActionModal.SetTargetsModal, data);
            break;

        // Zmień wersję procesu
        case FrontAction.ChangeProcess:
            $events.$emit(FrontActionModal.ChangeProcessModal, data);
            break;

        // Podproces
        case FrontAction.Subprocess:
            onSubprocess(data);
            break;

        // Odebranie uprawnień do dokumentu
        case FrontAction.RemoveParticipants:
            $events.$emit(FrontActionModal.RemoveParticipantsModal, data);
            break;

        // Zapoznałem się
        case FrontAction.MarkShareAsReadModal:
            $events.$emit(FrontActionModal.MarkShareAsReadModal, data);
            break;

        // Aktualizacja pól dokumentu
        case FrontAction.ChangeDocumentData:
            $events.$emit(FrontActionModal.ChangeDocumentDataModal, data);
            break;

        // Odblokuj dokument
        case FrontAction.UnblockDocumentModal:
            $events.$emit(FrontActionModal.UnblockDocumentModal, data);
            break;
    }
};

const onSubprocess = async (data: BpmnEmitOptions) =>
{
    const response = await GridService.fetchSubprocessActionData(props.url, props.publicId, data.buttonId);

    response.fieldKey && store.setFieldKey(response.fieldKey);
    response.document && store.setDocument(response.document);

    if (data.displayMode === 'Form')
    {
        router.push({
            name: `module-form`,
            params: { licence: response.licence },
            query: { id: response.endpointId },
        });
    }
    else
    {
        const newData: BpmnEmitOptions = {
            ...data,
            buttonId: response.endpointId,
            licence: response.licence,
            actionName: 'bpmn-start',
            id: '',
        };

        $events.$emit(`${FrontActionModal.Object}-1`, newData);
    }
};

const handleDelete = (id: string) =>
{
    $events.$emit('delete-entity-modal', { id, url: props.url });
};
</script>

<template>
    <template v-if="!admin">
        <template v-for="action in filteredActions" :key="action.key">
            <ActionButton
                toolbar
                :icon="getIconButton(action.type as string)"
                :variant="getVariantButton(action.type as string)"
                :disabled="!action.can"
                :text="action.value"
                @click="handleEvent(action)"
            />
        </template>
        <ideo-dropdown
            v-if="isOffice && filteredActionsOfficeChangeProcess.length"
            variant="warning"
            class="me-2"
            :icon="getIconButton('groupChangeTargets')"
            boundry="#view"
            no-caret
        >
            <template #button-content>
                <span class="fas fa-user-pen"></span>
                <span class="ms-2">{{ $t('[[[Zmień proces dla tego dokumentu]]]') }}</span>
            </template>
            <template #default>
                <ideo-dropdown-item-button v-for="action in filteredActionsOfficeChangeProcess" :key="action.key" @click="handleEvent(action)">
                    {{ action.value }}
                </ideo-dropdown-item-button>
            </template>
        </ideo-dropdown>
    </template>
    <template v-else>
        <ideo-dropdown
            v-if="filteredActions.length"
            variant="dark"
            :tooltip="$t('[[[Akcje administracyjne]]]')"
            boundry="#view"
            tooltip-position="top"
            :dropleft="!phone"
            no-caret
        >
            <template #button-content>
                <i class="fa-solid fa-gear"></i>
            </template>
            <template #default>
                <ideo-dropdown-item v-if="!filteredActions.length">
                    {{ $t('[[[Brak akcji]]]') }}
                </ideo-dropdown-item>
                <ideo-dropdown-item-button v-for="action in filteredActions" :key="action.key" @click="handleEvent(action)">
                    {{ action.value }}
                </ideo-dropdown-item-button>
            </template>
        </ideo-dropdown>
    </template>
</template>
